import React from 'react';
import { Card, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { Package, RefreshCw, Check, CheckCircle } from 'lucide-react';
import type { UseMutationResult } from '@tanstack/react-query';
import type { SkinSeoulAdminProduct } from '../../../../../models/ProductTypes';
import type { CMWorkflowStatus } from '../../../../../models/CatalogManagement';
import { IdBadge, IdBadgeType } from '../../../../../components/ui/badge/IdBadge';

interface CMPublishSectionProps {
  publishProducts: SkinSeoulAdminProduct[];
  publishLoading: boolean;
  publishingSkus: Set<string>;
  publishMutation: UseMutationResult<any, Error, any, unknown>;
  handlePublishProduct: (sku: string) => Promise<void>;
  handlePublishProducts: (products: SkinSeoulAdminProduct[]) => Promise<void>;
  fetchPublishProducts: (forceRefresh?: boolean) => Promise<void>;
  cmWorkflowData: any;
  isWorkflowStepCompleted: (data: any, status: CMWorkflowStatus) => boolean;
  CMWorkflowStatus: typeof CMWorkflowStatus;
}

export const CMPublishSection: React.FC<CMPublishSectionProps> = ({
  publishProducts,
  publishLoading,
  publishingSkus,
  publishMutation,
  handlePublishProduct,
  handlePublishProducts,
  fetchPublishProducts,
  cmWorkflowData,
  isWorkflowStepCompleted,
  CMWorkflowStatus,
}) => {
  return (
    <Card className="mb-4">
      <Card.Header>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <h6 className="mb-0">Publish products</h6>
            {isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.CM_PUBLISHED) && (
              <Badge bg="success" className="d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                <Check size={12} />
                Completed
              </Badge>
            )}
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => fetchPublishProducts(true)}
            disabled={publishLoading}
            className="d-flex align-items-center gap-1"
          >
            <RefreshCw size={14} className={publishLoading ? 'spin' : ''} />
            Refresh
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {isWorkflowStepCompleted(cmWorkflowData, CMWorkflowStatus.CM_PUBLISHED) && (
          <div className="text-center py-3 mb-3">
            <Alert variant="success" className="mb-0">
              <CheckCircle size={24} className="me-2" />
              <strong>Product Published!</strong>
              <br />
              <span className="text-muted">
                This product has successfully gone through all catalog management stages and is now live.
              </span>
            </Alert>
          </div>
        )}

        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
              <strong>
                Review and publish products. Go to the product link to check if it has been created and visible
              </strong>
            </div>
            {publishProducts.length > 0 && publishProducts.some((p) => p.postStatus !== 'publish') && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => handlePublishProducts(publishProducts.filter((p) => p.postStatus !== 'publish'))}
                disabled={publishMutation.isPending || publishProducts.every((p) => p.postStatus === 'publish')}
                className="d-flex align-items-center gap-1"
              >
                {publishMutation.isPending ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Publishing All...
                  </>
                ) : (
                  <>
                    <Check size={14} />
                    Publish All ({publishProducts.filter((p) => p.postStatus !== 'publish').length})
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {publishLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-2">Loading products...</p>
          </div>
        ) : publishProducts.length === 0 ? (
          <Alert variant="warning">
            <Package size={18} className="me-2" />
            No products found. Please ensure products were uploaded to WordPress in Step 3.
          </Alert>
        ) : (
          <div className="d-flex flex-column gap-3">
            {publishProducts.map((product) => {
              const isPublished = product.postStatus === 'publish';
              const isPublishing = publishingSkus.has(product.sku);
              return (
                <Card key={product.sku} className="border">
                  <Card.Body>
                    <div className="d-flex align-items-start justify-content-between gap-3">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <strong>{product.name || 'Untitled Product'}</strong>
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                          <div className="mb-2 d-flex align-items-center gap-2">
                            <IdBadge type={IdBadgeType.SKU} value={product.sku} size="sm" />
                          </div>
                          {product.productLink && (
                            <>
                              {(() => {
                                const isPublished = product.postStatus === 'publish';
                                const isInvalidUrl =
                                  product.productLink.endsWith('product//') ||
                                  product.productLink.endsWith('product/') ||
                                  product.productLink.endsWith('product');
                                return isInvalidUrl && isPublished ? (
                                  <Alert variant="danger" className="mb-2 py-2">
                                    <strong>⚠️ Invalid Product URL:</strong> Something went wrong. The product URL is
                                    missing the slug.
                                    <br />
                                    <small>{product.productLink}</small>
                                  </Alert>
                                ) : (
                                  <div className="mb-2" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                    <strong>Product Link:</strong>{' '}
                                    <a href={product.productLink} target="_blank" rel="noopener noreferrer">
                                      {product.productLink}
                                    </a>
                                  </div>
                                );
                              })()}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {isPublished ? (
                          <Badge bg="success" className="d-flex align-items-center gap-1 px-3 py-2">
                            <CheckCircle size={16} />
                            Published
                          </Badge>
                        ) : (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handlePublishProduct(product.sku)}
                            disabled={isPublishing}
                            className="d-flex align-items-center gap-1"
                          >
                            {isPublishing ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Publishing...
                              </>
                            ) : (
                              <>
                                <Check size={14} />
                                Publish
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}

        {publishProducts.length > 0 && (
          <div className="mt-4">
            <Alert variant="info">
              <strong>Note:</strong> You can publish products individually as needed. Click &quot;Click to
              Complete&quot; above to finalize this step when ready.
            </Alert>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
